import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { LanguageService } from 'src/app/shared/services/language.service';
import { LoginService } from '../../services/login.service';

@Component({
  selector: 'app-magic-link-request',
  templateUrl: './magic-link-request.component.html',
  styleUrls: ['./magic-link-request.component.scss'],
  standalone: true,
  imports: [TranslateModule, FormsModule],
})
export class MagicLinkRequestComponent implements OnDestroy {
  @Input() redirectPath: string | undefined;
  @Input() prefillEmail: string | undefined;
  @Output() changePage = new EventEmitter<{
    to?: 'login' | 'register' | 'reset' | 'magicRequest';
    email?: string;
  }>();

  email = '';
  isEmailEmpty = false;
  sent = false;
  sending = false;
  cooldownSeconds = 0;
  private intervalId: ReturnType<typeof setInterval> | null = null;

  private recaptchaV3Service = inject(ReCaptchaV3Service);
  private languageService = inject(LanguageService);
  private loginService = inject(LoginService);
  private router = inject(Router);

  constructor() {
    // initialize prefill if provided later by Angular input binding (handled in setter below if needed)
    Promise.resolve().then(() => {
      if (this.prefillEmail && !this.email) {
        this.email = (this.prefillEmail || '').trim().toLowerCase();
      }
      // Derive redirect when not provided or left as default sentinel 'account'
      if (!this.redirectPath || this.redirectPath === 'account') {
        let current = this.router.url; // includes leading slash and possibly language segment
        const lang = this.languageService.userSelectedLanguage.code;
        if (current.startsWith('/')) current = current.substring(1);
        if (current.startsWith(lang + '/')) {
          current = current.substring(lang.length + 1); // strip lang prefix
        }
        // Avoid empty (home) -> keep 'account' fallback
        if (current && current !== 'account') {
          this.redirectPath = current;
        }
      }
    });
  }

  ngOnDestroy(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  backToLogin() {
    this.changePage.emit({ to: 'login' });
  }

  send() {
    const email = this.email?.trim().toLowerCase();
    if (!email) {
      this.isEmailEmpty = true;
      return;
    }
    if (this.cooldownSeconds > 0 || this.sending) {
      return;
    }

    this.isEmailEmpty = false;
    this.sending = true;

    this.recaptchaV3Service
      .execute('magicLogin')
      .subscribe((_token: string) => {
        const languageCode = this.languageService.userSelectedLanguage
          .code as string;
        this.loginService
          .requestMagicLogin({
            email,
            languageCode,
            redirectPath: this.redirectPath,
          })
          .subscribe({
            next: () => {
              this.sent = true;
              this.sending = false;
              this.startCooldown(120);
            },
            error: () => {
              this.sending = false;
            },
          });
      });
  }

  onSubmit() {
    this.send();
  }

  private startCooldown(seconds: number) {
    this.cooldownSeconds = seconds;
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
    this.intervalId = setInterval(() => {
      this.cooldownSeconds -= 1;
      if (this.cooldownSeconds <= 0) {
        if (this.intervalId) {
          clearInterval(this.intervalId);
        }
        this.intervalId = null;
      }
    }, 1000);
  }
}
